import { ArrowDownward, ArrowUpward, DeleteSweep as DeleteSweepIcon, Search as SearchIcon, Splitscreen as SplitscreenIcon, Layers as LayersIcon } from '@mui/icons-material';
import { Box, Chip, IconButton, InputAdornment, List, ListItemButton, TextField, Typography } from '@mui/material';
import { formatResourceName, formatTime } from './utils';

const borderColor = '#30363d';
const accentColor = '#58a6ff';

export const SidebarList = ({ messages, displayMessages, selectedId, setSelectedId, sidebarWidth, sortOrder, setSortOrder, isFlattened, setIsFlattened, searchInput, setSearchInput, handleClear, windowWidth, listEndRef }) => {
  return (
    <Box sx={{ width: selectedId ? `${sidebarWidth}%` : '100%', display: 'flex', flexDirection: 'column', borderRight: `1px solid ${borderColor}` }}>
      {/* Header Top Bar */}
      <Box sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='h6' sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          (;´༎ຶД༎ຶ`) WebSocket: Because REST is Too Hard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => setIsFlattened(!isFlattened)}
            sx={{ color: isFlattened ? accentColor : '#8b949e' }}
            title={isFlattened ? 'Group Request/Response' : 'Flatten Request/Response'}
          >
            {isFlattened ? <LayersIcon fontSize='small' /> : <SplitscreenIcon fontSize='small' />}
          </IconButton>
          <IconButton onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} sx={{ color: '#8b949e' }} title={sortOrder === 'asc' ? 'Sort: Newest at Bottom' : 'Sort: Newest at Top'}>
            {sortOrder === 'asc' ? <ArrowDownward fontSize='small' /> : <ArrowUpward fontSize='small' />}
          </IconButton>
          <IconButton onClick={handleClear} sx={{ color: '#8b949e' }} title='Clear Log'>
            <DeleteSweepIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Search Input Row */}
      <Box sx={{ px: 2, pb: 2, borderBottom: `1px solid ${borderColor}` }}>
        <TextField
          size='small'
          fullWidth
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder='Filter by path, method, payload content, ID, trigger page...'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' sx={{ color: '#8b949e' }} />
              </InputAdornment>
            ),
            sx: {
              height: 32,
              fontSize: '13px',
              color: '#e6edf3',
              backgroundColor: '#010409',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: borderColor,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#8b949e',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: accentColor,
              },
            },
          }}
        />
      </Box>

      {/* Messages List */}
      <List sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
        {messages.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: '#8b949e' }}>Listening for WebSocket traffic...</Box>
        ) : (
          displayMessages.map((item) => (
            <ListItemButton
              key={item.uniqueKey}
              selected={item.id === selectedId}
              onClick={() => setSelectedId(item.id)}
              sx={{
                borderBottom: `1px solid ${borderColor}`,
                bgcolor: item.id === selectedId ? 'rgba(88, 166, 255, 0.1)' : 'transparent',
                '&.Mui-selected': { bgcolor: 'rgba(88, 166, 255, 0.15)' },
                '&:hover': { bgcolor: 'rgba(88, 166, 255, 0.05)' },
                py: 1.5,
                px: 2,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 2, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexGrow: 1 }}>
                {item.type === 'request' ? (
                  <Chip
                    size='small'
                    label={`REQ: ${item.method || 'UNKNOWN'}`}
                    sx={{
                      height: 18,
                      fontSize: 10,
                      bgcolor: '#1f6feb',
                      color: '#ffffff',
                      border: 'none',
                      flexShrink: 0,
                    }}
                  />
                ) : item.type === 'response' ? (
                  <Chip
                    size='small'
                    label={`RES: ${item.method || 'UNKNOWN'}`}
                    sx={{
                      height: 18,
                      fontSize: 10,
                      bgcolor: item.status === 'error' ? '#da3633' : '#238636',
                      color: '#ffffff',
                      border: 'none',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Chip
                    size='small'
                    label={item.method || 'UNKNOWN'}
                    sx={{
                      height: 18,
                      fontSize: 10,
                      bgcolor: '#21262d',
                      color: '#c9d1d9',
                      border: `1px solid ${borderColor}`,
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography
                  variant='body2'
                  title={`Resource Path: ${item.resource}\nTrigger Page: ${item.triggerUrl || 'Unknown / Standalone'}`}
                  sx={{
                    fontWeight: 500,
                    color: item.status === 'error' ? '#f85149' : '#e6edf3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {formatResourceName(item.resource, sidebarWidth, !!selectedId, windowWidth)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                <Typography variant='caption' sx={{ color: '#8b949e', fontFamily: 'Consolas, monospace', fontSize: '11px' }}>
                  {formatTime(item.timestamp)}
                </Typography>
                <Typography
                  variant='caption'
                  sx={{
                    color:
                      item.type === 'request'
                        ? '#8b949e'
                        : item.status === 'pending'
                        ? '#8b949e'
                        : item.duration > 1000
                        ? '#d29922'
                        : '#3fb950',
                    fontWeight: 600,
                    minWidth: '60px',
                    textAlign: 'right',
                  }}
                >
                  {item.type === 'request'
                    ? 'sent'
                    : item.status === 'pending'
                    ? 'waiting'
                    : item.duration !== undefined
                    ? `${item.duration} ms`
                    : '-'}
                </Typography>
              </Box>
            </ListItemButton>
          ))
        )}
        <div ref={listEndRef} />
      </List>
    </Box>
  );
};
